import React, { useRef, useEffect, useState } from "react";
import { Tooltip, tooltipClasses } from "@mui/material";
import { styled } from "@mui/system";

const LightTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "white",
    color: "var(--header-page-name)",
    fontSize: 11,
    fontWeight: 500,
    lineHeight: "16px",
    letterSpacing: "0.5px",
    boxShadow:
      "0px 1px 2px 0px #2b2b2b12, 0px 3px 3px 0px #2b2b2b0f, 0px 7px 4px 0px #2b2b2b0a, 0px 13px 5px 0px #2b2b2b03, 0px 20px 6px 0px #2b2b2b00",
  },
}));

const TooltipText = ({
  text,
  className = "",
  multiline = false,
  maxWidth = "150",
  always = false,
  fullWidth = false,
  placement = "top",
}) => {
  const textRef = useRef(null);
  const [isEllipsisActive, setIsEllipsisActive] = useState(false);
  const displayText = (() => {
    if (text == null) return "";
    if (typeof text === "string") return text;
    if (typeof text === "number") return text.toLocaleString();
    if (typeof text === "boolean") return text ? "True" : "False";
    try {
      return JSON.stringify(text);
    } catch (_) {
      return String(text);
    }
  })();

  const rafId = useRef(null);
  const checkOverflow = () => {
    if (rafId.current) cancelAnimationFrame(rafId.current);
    rafId.current = requestAnimationFrame(() => {
      if (!textRef.current) return;
      let next = false;
      if (multiline) {
        next = textRef.current.scrollHeight > textRef.current.clientHeight;
      } else {
        const selfOverflow =
          textRef.current.scrollWidth > textRef.current.clientWidth;
        const parent = textRef.current.parentElement;
        const parentOverflow = parent
          ? parent.scrollWidth > parent.clientWidth
          : false;
        next = selfOverflow || parentOverflow;
      }
      setIsEllipsisActive((prev) => (prev !== next ? next : prev));
    });
  };

  useEffect(() => {
    checkOverflow();
    window.addEventListener("resize", checkOverflow, { passive: true });
    return () => {
      window.removeEventListener("resize", checkOverflow);
      if (rafId.current) cancelAnimationFrame(rafId.current);
    };
  }, [text]);

  const style = multiline
    ? {
        maxWidth: "100%",
        overflow: "hidden",
        verticalAlign: "middle",
      }
    : fullWidth
    ? {
        display: "block",
        width: "100%",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        verticalAlign: "middle",
      }
    : {
        display: "inline-block",
        maxWidth: `${maxWidth}px`,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        verticalAlign: "middle",
      };

  return (
    <LightTooltip
      title={always || isEllipsisActive ? displayText : ""}
      arrow
      placement={placement}
    >
      <span ref={textRef} className={`${className} tooltip-text`} style={style}>
        {displayText}
      </span>
    </LightTooltip>
  );
};

export default TooltipText;
